import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@trillion/ui/components/ui/dialog";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Separator } from "@trillion/ui/components/ui/separator";
import { ScrollArea } from "@trillion/ui/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { ApiError } from "@trillion/ui/lib/api-client";
import { toast } from "sonner";
import { api } from "../lib/api";
import { serverError } from "../lib/server-error";
import type { AccessRequest, DiagramInvite, DiagramPermission, DiagramShareOwner, Team } from "../types/board";
import { DiagramRole, InviteStatus } from "../types/enums";

// Enum-keyed so a new member is a compile error. Render the label: a numeric enum interpolates as its integer.
const diagramRoleLabels: Record<DiagramRole, string> = {
  [DiagramRole.Owner]: "Owner",
  [DiagramRole.Editor]: "Editor",
  [DiagramRole.Commenter]: "Commenter",
  [DiagramRole.Viewer]: "Viewer",
};

// Sharing never grants Owner; Editor is the most a grant (and a team-admin manager) can confer.
const grantableRoles = [DiagramRole.Viewer, DiagramRole.Commenter, DiagramRole.Editor] as const;

interface ShareDialogProps {
  diagramId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the pending access-request count after every successful load of the requests. */
  onPendingCountChange?: (count: number) => void;
}

interface InviteResult { status: "granted" | "invite_pending" }

/** A grantee's display name, never its id: a user's name then email, a team's name. */
function granteeName(permission: DiagramPermission): string {
  return permission.display_name || permission.email || (permission.grantee_type === "team" ? "Unknown team" : "Unknown user");
}

/** A requester's display name, never their id: name, then email. */
function requesterName(request: AccessRequest): string {
  return request.display_name || request.email || "Unknown user";
}

function memberOnlyHint(teamName: string): string {
  return `Only members of ${teamName} can change its role`;
}

function RoleSelect({ id, label, value, disabled, onChange }: {
  id?: string; label?: string; value: DiagramRole; disabled?: boolean; onChange: (role: DiagramRole) => void;
}) {
  return (
    <Select value={String(value)} disabled={disabled} onValueChange={(v) => onChange(Number(v) as DiagramRole)}>
      <SelectTrigger id={id} aria-label={label} className="h-8 w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {grantableRoles.map((role) => (
          <SelectItem key={role} value={String(role)}>
            {diagramRoleLabels[role]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ShareDialog({ diagramId, open, onOpenChange, onPendingCountChange }: ShareDialogProps) {
  const [owner, setOwner] = useState<DiagramShareOwner | null>(null);
  const [permissions, setPermissions] = useState<DiagramPermission[]>([]);
  const [invites, setInvites] = useState<DiagramInvite[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [teamChoice, setTeamChoice] = useState("");
  const [addRole, setAddRole] = useState<DiagramRole>(DiagramRole.Viewer);
  const [isAdding, setIsAdding] = useState(false);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [approveRoles, setApproveRoles] = useState<Record<string, DiagramRole>>({});
  const [reviewing, setReviewing] = useState<string | null>(null);
  // A ref, so a new callback identity from the page never re-runs the open effect's loads.
  const pendingCountRef = useRef(onPendingCountChange);
  pendingCountRef.current = onPendingCountChange;

  const loadShares = useCallback(async () => {
    try {
      const [shares, pending] = await Promise.all([
        api<{ owner: DiagramShareOwner; permissions: DiagramPermission[] }>(`/api/diagrams/${diagramId}/permissions`),
        api<{ invites: DiagramInvite[] }>(`/api/diagrams/${diagramId}/invites`),
      ]);
      setOwner(shares.owner);
      setPermissions(shares.permissions);
      setInvites(pending.invites.filter((invite) => invite.invite_status_id === InviteStatus.Pending));
    } catch {
      toast.error("Failed to load sharing");
    }
  }, [diagramId]);

  // Loaded apart from the shares: a failure here leaves the rest of the dialog working.
  const loadRequests = useCallback(async () => {
    try {
      const res = await api<{ access_requests: AccessRequest[] }>(`/api/diagrams/${diagramId}/access-requests`);
      setRequests(res.access_requests);
      pendingCountRef.current?.(res.access_requests.length);
    } catch {
      toast.error("Failed to load access requests");
    }
  }, [diagramId]);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    const loadTeams = api<{ teams: Team[] }>("/api/teams")
      .then((res) => setTeams(res.teams))
      .catch(() => toast.error("Failed to load your teams"));
    Promise.all([loadShares(), loadTeams, loadRequests()]).finally(() => setIsLoading(false));
  }, [open, loadShares, loadRequests]);

  const myTeamIds = new Set(teams.map((team) => team.team_id));
  const isMyTeam = (permission: DiagramPermission) =>
    permission.grantee_type !== "team" || myTeamIds.has(permission.grantee_id);

  async function grant(granteeType: "user" | "team", granteeId: string, role: DiagramRole, teamName?: string) {
    try {
      await api(`/api/diagrams/${diagramId}/permissions`, {
        method: "POST",
        body: JSON.stringify({ grantee_type: granteeType, grantee_id: granteeId, diagram_role_id: role }),
      });
      return true;
    } catch (err) {
      const forbiddenTeam = granteeType === "team" && err instanceof ApiError && err.status === 403;
      toast.error(forbiddenTeam ? memberOnlyHint(teamName ?? "this team") : serverError(err, "Failed to share"));
      return false;
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address && !teamChoice) return;
    setIsAdding(true);
    try {
      if (address) {
        try {
          const result = await api<InviteResult>(`/api/diagrams/${diagramId}/invite`, {
            method: "POST",
            body: JSON.stringify({ email: address, diagram_role_id: addRole }),
          });
          toast.success(result.status === "invite_pending" ? `Invite sent to ${address}` : `Shared with ${address}`);
          setEmail("");
        } catch (err) {
          toast.error(serverError(err, "Failed to share"));
          return;
        }
      } else {
        const team = teams.find((t) => t.team_id === teamChoice);
        if (!await grant("team", teamChoice, addRole, team?.name)) return;
        toast.success(`Shared with ${team?.name ?? "the team"}`);
        setTeamChoice("");
      }
      await loadShares();
    } finally {
      setIsAdding(false);
    }
  }

  async function handleRoleChange(permission: DiagramPermission, role: DiagramRole) {
    if (!await grant(permission.grantee_type, permission.grantee_id, role, granteeName(permission))) return;
    toast.success("Role updated");
    await loadShares();
  }

  async function handleRemove(permission: DiagramPermission) {
    try {
      await api(`/api/diagrams/${diagramId}/permissions/${permission.diagram_permission_id}`, { method: "DELETE" });
      setPermissions((prev) => prev.filter((p) => p.diagram_permission_id !== permission.diagram_permission_id));
      toast.success("Access removed");
    } catch (err) {
      toast.error(serverError(err, "Failed to remove access"));
    }
  }

  async function handleCancelInvite(invite: DiagramInvite) {
    try {
      await api(`/api/diagrams/${diagramId}/invites/${invite.invite_id}`, { method: "DELETE" });
      setInvites((prev) => prev.filter((i) => i.invite_id !== invite.invite_id));
      toast.success("Invite cancelled");
    } catch (err) {
      toast.error(serverError(err, "Failed to cancel invite"));
    }
  }

  async function handleReview(request: AccessRequest, decision: "approve" | "deny") {
    const name = requesterName(request);
    setReviewing(request.request_id);
    try {
      if (decision === "approve") {
        const role = approveRoles[request.request_id] ?? DiagramRole.Viewer;
        await api(`/api/access-requests/${request.request_id}/approve`, {
          method: "PUT",
          body: JSON.stringify({ diagram_role_id: role }),
        });
        toast.success(`Approved ${name}`);
        await Promise.all([loadShares(), loadRequests()]);
      } else {
        await api(`/api/access-requests/${request.request_id}/deny`, { method: "PUT" });
        toast.success(`Denied ${name}`);
        await loadRequests();
      }
    } catch (err) {
      // A 409 means another manager reviewed it first; the reload shows the current list.
      toast.error(serverError(err, "Failed to review the request"));
      await loadRequests();
    } finally {
      setReviewing(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Diagram</DialogTitle>
          <DialogDescription>Choose who can open this diagram and what they can do.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {requests.length > 0 && (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Access requests</p>
                <ul className="space-y-2">
                  {requests.map((request) => {
                    const name = requesterName(request);
                    // One review at a time: a second click while any approve/deny is in flight would reset `reviewing`
                    // to the newer id and re-enable the first row's controls mid-request.
                    const busy = reviewing !== null;
                    return (
                      <li key={request.request_id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm">{name}</p>
                          {request.display_name && request.email && (
                            <p className="truncate text-xs text-muted-foreground">{request.email}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <RoleSelect
                            label={`Approve role for ${name}`}
                            value={approveRoles[request.request_id] ?? DiagramRole.Viewer}
                            disabled={busy}
                            onChange={(role) => setApproveRoles((prev) => ({ ...prev, [request.request_id]: role }))}
                          />
                          <Button
                            size="sm"
                            aria-label={`Approve ${name}`}
                            className="h-8"
                            disabled={busy}
                            onClick={() => handleReview(request, "approve")}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Deny ${name}`}
                            className="h-8"
                            disabled={busy}
                            onClick={() => handleReview(request, "deny")}
                          >
                            Deny
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <Separator />
            </>
          )}

          <form onSubmit={handleAdd} className="space-y-3">
            <p className="text-sm font-medium">Add people or teams</p>
            <div className="space-y-2">
              <Label htmlFor="share-email">Email</Label>
              <Input
                id="share-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (e.target.value) setTeamChoice(""); }}
                placeholder="colleague@company.com"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="share-team">Team</Label>
                <Select
                  value={teamChoice}
                  disabled={teams.length === 0}
                  onValueChange={(value) => { setTeamChoice(value); if (value) setEmail(""); }}
                >
                  <SelectTrigger id="share-team" className="h-8">
                    <SelectValue placeholder="Pick a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-role">Role</Label>
                <RoleSelect id="share-role" value={addRole} onChange={setAddRole} />
              </div>
              <Button type="submit" size="sm" disabled={isAdding || (!email.trim() && !teamChoice)}>
                Add
              </Button>
            </div>
          </form>

          <Separator />

          <div className="space-y-2">
            <p className="text-sm font-medium">People and teams with access</p>
            {isLoading && !owner ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : (
              <ScrollArea className="max-h-72">
                <ul className="space-y-2">
                  {owner && (
                    <li className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div>
                        <p className="text-sm">{owner.display_name || owner.email || "Unknown user"}</p>
                        {owner.display_name && owner.email && (
                          <p className="text-xs text-muted-foreground">{owner.email}</p>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">Owner</span>
                    </li>
                  )}
                  {permissions.map((permission) => {
                    const name = granteeName(permission);
                    const editable = isMyTeam(permission);
                    return (
                      <li key={permission.diagram_permission_id} className="rounded-md border px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 text-sm">
                              <span className="truncate">{name}</span>
                              {permission.grantee_type === "team" && <Badge variant="secondary">Team</Badge>}
                            </p>
                            {permission.grantee_type === "user" && permission.display_name && permission.email && (
                              <p className="text-xs text-muted-foreground">{permission.email}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <RoleSelect
                              label={`Role for ${name}`}
                              value={permission.diagram_role_id}
                              disabled={!editable}
                              onChange={(role) => handleRoleChange(permission, role)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Remove ${name}`}
                              className="h-8 text-destructive hover:text-destructive"
                              onClick={() => handleRemove(permission)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                        {!editable && (
                          <p className="mt-1 text-xs text-muted-foreground">{memberOnlyHint(name)}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
          </div>

          {invites.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Pending invites</p>
              <ul className="space-y-2">
                {invites.map((invite) => (
                  <li key={invite.invite_id} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{invite.email}</span>
                      <Badge variant="outline">{diagramRoleLabels[invite.diagram_role_id]}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Cancel invite for ${invite.email}`}
                      className="h-8"
                      onClick={() => handleCancelInvite(invite)}
                    >
                      Cancel
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
